/**
 * The scale a group's scores are drawn against.
 *
 * Scores used to be capped at 1, so every colour ramp and bar track could
 * assume the same full scale. Metrics like SNR have no ceiling, so the far end
 * comes from the data instead — but only once the data actually passes full
 * scale. A rate keeps its 1, so 0.87 draws 87% of the track and tints 87% of
 * the way up the ramp, with the headroom above it visible in both.
 *
 * Length and colour read the SAME scale, anchored at zero. Both encode
 * magnitude, so a bar that is 87% long and a tint that is 87% up the ramp are
 * saying one thing twice rather than two things that can disagree. That
 * matters most in the matrix, where there are no bars and the tint is the only
 * visual encoding a cell has.
 */

/**
 * Full scale for a rate, in RAW score space — the ceiling a pass rate, an
 * accuracy or a C-index can never pass. Distinct from `climb.FULL_SCALE`,
 * which is the same idea on the ×100 axis the charts plot in.
 */
const RATE_CEILING = 1

/**
 * Far end of the scale for `scores`: full scale, unless the data passes it.
 *
 * A rate keeps its 1 ceiling. Scaling those scores to their own max instead
 * would redraw the group's best task as complete no matter what it scored, and
 * every bar and tint would shift the moment a better run landed. Only a metric
 * that genuinely exceeds full scale sets its own ceiling — there is nothing
 * else to measure an SNR of 13.3 against. Same rule as `climb.scoreDomain` and
 * the distribution chart's `plotTop`, one space down.
 *
 * An empty set falls back to full scale, so a group with no runs still renders
 * its axes.
 *
 * Known limit: an unbounded metric whose scores all happen to sit below 1 gets
 * a rate's scale. That is not decidable from the data — nothing in the group
 * contract declares boundedness, and `score_format` cannot stand in (a C-index
 * is `decimal` and bounded, an SNR is `decimal` and is not). The cost is a
 * track and a ramp that under-use their range; the ticks still print real
 * values and the score beside each bar is exact, so nothing misreads. Deciding
 * it properly means a new per-group field, not a cleverer guess. Accepted as a
 * rare case in review on #449.
 */
export function scoreCeiling(scores: number[]): number {
  return Math.max(RATE_CEILING, ...scores)
}

/**
 * Where `score` sits on the scale, in [0,1] — the bar's length and the colour
 * ramp's input alike. Measured from zero, so it stays proportional to the
 * value: anchoring at the group's minimum would draw the lowest score as
 * nothing and tint it pure red however good it was.
 */
export function scalePosition(score: number, ceiling: number): number {
  return Math.min(Math.max(score / ceiling, 0), 1)
}

/**
 * The values under a bar track, left to right: `count` evenly spaced stops
 * from 0 to the ceiling. Positions stay fixed (0%, 25%, … 100%), so a rate
 * keeps the familiar `0 / 0.25 / 0.5 / 0.75 / 1` and only an unbounded metric
 * relabels them.
 */
export function scoreTicks(ceiling: number, count = 5): number[] {
  return Array.from({ length: count }, (_, index) => (ceiling * index) / (count - 1))
}
